import React from "react";

import {
	SCORING_COMPLETE_TASK,
	SCORING_PARTIAL_TASK,
	SCORING_FAILED_TASK,
	SCORING_FAILED_FALLBACK_MESSAGE,
	isPredictiveScoringTask,
	isPartialScoringTask,
	isFailedScoringTask
} from "../socket/formatters/predictiveScoringTask";
import useFTMessageGrowl from "hooks/useFTMessageGrowl";
import VersionImportErrorGrowl from "widgets/messages/VersionImportErrorGrowl";
import VersionExportGrowl from "widgets/messages/VersionExportGrowl";
import VersionImportPreviewGrowl from "widgets/messages/VersionImportPreviewGrowl";

function EXPORT_VERSIONS_NOTIFICATIONS(detail){
	let { task, status, data, downloadLink } = detail?.message || {};
	let { filename, description } = data?.request || {};

	if(/Export versions/i.test(task) && /Success/i.test(status)){
		return {
			type: "success",
			messageHeader: "Success",
			formatter: () => <VersionExportGrowl downloadLink={downloadLink} filename={filename} description={description} />,
		};
	}
}

function IMPORT_SOCIAL_NOTIFICATIONS(detail){
	let { task, status, data } = detail?.message || {};

	if(/Import social creatives/i.test(task) && /Success/i.test(status)){
		let { feedback = "Social creatives successfully imported" } = data?.response || {};

		return {
			type: "success",
			messageHeader: "Success!",
			message: feedback,
		};
	}
}

function IMPORT_VERSIONS_NOTIFICATIONS(detail) {
	let { task, status, data = {} } = detail?.message || {};

	if (!/^import versions$/gi.test(task)) { return; }

	if (/in progress/gi.test(status)) {
		return {
			type: "success",
			messageHeader: "Importing versions in progress",
		};
	}

	if (/failed/gi.test(status)) {
		const { request = {} } = data;
		const { filename = "" } = request;
		const { error } = data?.response || {};

		return {
			type: "error",
			messageHeader: `Error importing versions sheet`,
			formatter: () => <VersionImportErrorGrowl filename={filename} error={error} />,
		};
	}

	if (/success/gi.test(status)) {
		let { feedback = "" } = data?.response || {};

		return {
			type: "success",
			messageHeader: "Importing versions successful!",
			message: feedback,
		};
	}
}

function IMPORT_VERSIONS_PREVIEW_NOTIFICATIONS(detail) {
	let { task, status, data = {}, jobId } = detail?.message || {};
	const filename = data.request?.filename || "versions";
	const libraryId = data.creativeLibrary?.id;

	if (!/^import versions with preview$/gi.test(task)) { return; }

	if (/failed/gi.test(status)) {
		const error = data.response?.error;

		return {
			type: "error",
			messageHeader: `Error importing versions sheet`,
			formatter: () => <VersionImportErrorGrowl filename={filename} error={error} />,
		};
	}

	if (/success/gi.test(status)) {
		const updatedCreatives = (data?.response?.changes?.creatives || []).filter((creative) => {
			const creativeChanges = creative.updated + creative.new;
			return creativeChanges > 0;
		});

		if(updatedCreatives.length){
			return {
				type: "success",
				messageHeader: "Success!",
				formatter: () => (
					<VersionImportPreviewGrowl
						filename = {filename}
						libraryId = {libraryId}
						jobId = {jobId}
					/>
				),
			};
		} else {
			return {
				type: "success",
				messageHeader: "Success!",
				message: "Import contains no new versions",
			};
		}
	}
}

function PREDICTIVE_SCORING_NOTIFICATIONS(detail) {
	let { task, status, data = {} } = detail?.message || {};

	if (!isPredictiveScoringTask(task)) { return; }

	// A total-failure run comes back as JOB_FAILED (status contains "failed"); surface it as an error
	// toast rather than dropping it. Complete/Partial keep arriving with a success status.
	if (isFailedScoringTask(task)) {
		return {
			type: "error",
			messageHeader: SCORING_FAILED_TASK,
			message: data?.failureReason || SCORING_FAILED_FALLBACK_MESSAGE,
		};
	}

	if (!/success/i.test(status)) { return; }

	const isPartial = isPartialScoringTask(task);
	return {
		type: isPartial ? "warn" : "success",
		messageHeader: isPartial ? SCORING_PARTIAL_TASK : SCORING_COMPLETE_TASK,
		message: isPartial ? "Scoring completed with some failures." : "Your creative scores are ready.",
	};
}

function DUPLICATE_IMPOSIUM_PROJECT_NOTIFICATIONS(detail) {
	let { task, status, data = {} } = detail?.message || {};
	const projectName = data?.imposiumProject?.name || "Imposium project";

	if (!/Imposium Project Duplication/gi.test(task)) { return; }

	if (/in progress/gi.test(status)) {
		return {
			type: "success",
			messageHeader: "Duplicating Imposium Project",
			message: `"${projectName}" is being created...`,
		};
	}

	if (/success/gi.test(status)) {
		return {
			type: "success",
			messageHeader: "Success!",
			message: `"${projectName}" has been created.`,
		};
	}
}

function IMPOSIUM_VERSION_RENDER_NOTIFICATIONS(detail) {
	const { status, data = {}, error } = detail?.message || {};

	//imposium rendering job messages are identical to creative status transition messages, except for the jobType field
	//we currently don't show growls for other types of status transitions, but we do receive websocket messages for them
	if (!/Imposium Version Render/i.test(data?.jobType)) {
		return;
	}

	//we currently only want to notify on error
	if (/failed/i.test(status)) {
		return {
			type: "error",
			messageHeader: "Imposium Version Render Failed",
			message: error?.description || "Could not complete Imposium version rendering job",
		};
	}
}

const GLOBAL_GROWL_MESSAGE_TRIGGERS = [
	EXPORT_VERSIONS_NOTIFICATIONS,
	IMPORT_SOCIAL_NOTIFICATIONS,
	IMPORT_VERSIONS_NOTIFICATIONS,
	IMPORT_VERSIONS_PREVIEW_NOTIFICATIONS,
	PREDICTIVE_SCORING_NOTIFICATIONS,
	DUPLICATE_IMPOSIUM_PROJECT_NOTIFICATIONS,
	IMPOSIUM_VERSION_RENDER_NOTIFICATIONS,
];

export function GlobalSocketGrowler(){
	useFTMessageGrowl(GLOBAL_GROWL_MESSAGE_TRIGGERS);

	// We don't actually need to render anything.  This is just a vehicle for the hook's effect.
	return null;
}
